import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from '../components/ui/button';
import { Eye, X, Clock, User, AlertTriangle, EyeOff, ShieldAlert, UserCheck } from 'lucide-react';

/**
 * Impersonation Banner — covers both session types:
 *   'client'  → amber banner  (View as Client + anonymise toggle)
 *   'auditor' → teal banner   (View as Auditor, auditor permissions enforced)
 */
const ImpersonationBanner = () => {
  const {
    impersonation, isImpersonating, isAuditorView,
    endImpersonation, loading, expired,
    isAnonymised, toggleAnonymised, anonymise,
  } = useImpersonation();
  const navigate = useNavigate();
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (impersonation?.remaining_minutes) setRemainingMinutes(impersonation.remaining_minutes);
    const interval = setInterval(() => setRemainingMinutes(prev => Math.max(0, prev - 1)), 60000);
    return () => clearInterval(interval);
  }, [impersonation?.remaining_minutes]);

  useEffect(() => {
    if (expired) navigate('/users');
  }, [expired, navigate]);

  if (!isImpersonating || !impersonation) return null;

  const handleExit = async () => {
    if (isAuditorView) {
      // For auditor view: wait for endImpersonation (which restores the admin's
      // auth context) before navigating, so the destination route sees isAdmin=true.
      await endImpersonation();
      navigate('/users');
    } else {
      // For client view: navigate immediately, then clean up.
      navigate('/home');
      await endImpersonation();
    }
  };

  const handleToggleAnonymised = async () => {
    setToggling(true);
    try { await toggleAnonymised(); } finally { setToggling(false); }
  };

  const isWarning = remainingMinutes <= 5;

  // ── Auditor view banner (teal) ──────────────────────────────────────────────
  if (isAuditorView) {
    const bannerBg = isWarning ? 'bg-red-600' : 'bg-teal-700';
    const pillBg   = isWarning ? 'bg-red-700'  : 'bg-teal-800';
    const subText  = isWarning ? 'text-red-100' : 'text-teal-200';
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-[100] ${bannerBg} text-white px-4 py-2 shadow-lg`}
        data-testid="auditor-view-banner"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <div className={`flex items-center gap-2 ${pillBg} px-3 py-1 rounded-full`}>
              <UserCheck className="h-4 w-4" />
              <span className="font-semibold text-sm">Viewing as Auditor</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 shrink-0" />
              <span className="font-medium text-sm">{impersonation.client_user_name}</span>
              <span className={`${subText} text-sm hidden sm:inline`}>({impersonation.client_user_email})</span>
            </div>
            <div className={`flex items-center gap-1 ${subText} text-sm`}>
              {isWarning && <AlertTriangle className="h-3 w-3" />}
              <Clock className="h-3 w-3" />
              <span>{remainingMinutes}m remaining</span>
            </div>
            <div className="flex items-center gap-1 bg-white/20 border border-white/30 px-2 py-0.5 rounded text-xs">
              <ShieldAlert className="h-3 w-3" />
              Auditor permissions enforced
            </div>
          </div>
          <Button
            variant="outline" size="sm" onClick={handleExit} disabled={loading}
            className={`text-xs ${isWarning
              ? 'bg-white text-red-700 border-white hover:bg-red-50'
              : 'bg-white text-teal-700 border-white hover:bg-teal-50'}`}
            data-testid="exit-auditor-view-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" />Exit Auditor View
          </Button>
        </div>
      </div>
    );
  }

  // ── Client view banner (amber) ──────────────────────────────────────────────
  const bannerBg = isWarning ? 'bg-red-600' : isAnonymised ? 'bg-violet-700' : 'bg-amber-500';
  const pillBg   = isWarning ? 'bg-red-700'  : isAnonymised ? 'bg-violet-800' : 'bg-amber-600';
  const subText  = isWarning ? 'text-red-100' : isAnonymised ? 'text-violet-200' : 'text-amber-100';
  const displayName  = isAnonymised ? anonymise(impersonation.client_user_name, 'user') : impersonation.client_user_name;
  const displayEmail = isAnonymised ? 'demo@example.com' : impersonation.client_user_email;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-[100] ${bannerBg} text-white px-4 py-2 shadow-lg transition-colors duration-300`}
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`flex items-center gap-2 ${pillBg} px-3 py-1 rounded-full`}>
            <Eye className="h-4 w-4" />
            <span className="font-semibold text-sm">Viewing as Client</span>
          </div>
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 shrink-0" />
            <span className="font-medium text-sm">{displayName}</span>
            <span className={`${subText} text-sm hidden sm:inline`}>({displayEmail})</span>
          </div>
          <div className={`flex items-center gap-1 ${subText} text-sm`}>
            {isWarning && <AlertTriangle className="h-3 w-3" />}
            <Clock className="h-3 w-3" />
            <span>{remainingMinutes}m</span>
          </div>
          {isAnonymised && (
            <div
              className="flex items-center gap-1 bg-white/20 border border-white/40 px-2 py-0.5 rounded text-xs font-bold tracking-wide uppercase"
              data-testid="anonymised-demo-badge"
            >
              <ShieldAlert className="h-3 w-3" />Anonymised Demo Mode
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleAnonymised} disabled={toggling || loading}
            data-testid="anonymise-toggle-btn"
            title={isAnonymised ? 'Disable anonymisation' : 'Enable anonymisation'}
            className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-semibold border transition-all duration-200 disabled:opacity-50 ${
              isAnonymised
                ? 'bg-white text-violet-700 border-white hover:bg-violet-50'
                : 'bg-transparent text-white border-white/60 hover:bg-white/15'
            }`}
          >
            <EyeOff className={`h-3.5 w-3.5 ${isAnonymised ? '' : 'opacity-70'}`} />
            {isAnonymised ? 'Anonymised' : 'Anonymise'}
          </button>
          <Button
            variant="outline" size="sm" onClick={handleExit} disabled={loading}
            className={`text-xs ${isWarning
              ? 'bg-white text-red-700 border-white hover:bg-red-50'
              : isAnonymised
                ? 'bg-white text-violet-700 border-white hover:bg-violet-50'
                : 'bg-white text-amber-700 border-white hover:bg-amber-50'}`}
            data-testid="exit-impersonation-btn"
          >
            <X className="h-3.5 w-3.5 mr-1" />Exit View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
